import { catalog, type AssetQuery, type CatalogBackup } from "@focalfolio/catalog";
import {
  assetFromFileEntry,
  createDiagnosticReport,
  detectRuntimeCapabilities,
  planBatchRename,
  type RenameTemplateOptions
} from "@focalfolio/application-core";
import {
  BrowserWorkspaceFileSystem,
  forgetRegisteredRoot,
  pickDirectoryRoot,
  queryHandlePermission,
  registerDirectoryHandle,
  requestHandlePermission,
  restoreRegisteredRoots,
  type RegisteredRoot
} from "@focalfolio/fs-browser";
import { readMetadata, createLocalPreviewBlob } from "@focalfolio/metadata";
import { parseXmp, sidecarPathFor, writeXmpSidecarSafely, type XmpUpdate } from "@focalfolio/xmp";
import { DurableJobRunner, runBoundedBatch } from "@focalfolio/job-engine";
import {
  createContactSheetPdf,
  createWebGalleryZip,
  downloadBlob,
  exportAsset
} from "@focalfolio/export-engine";
import {
  DEFAULT_EXPORT_PRESET,
  parentPathOf,
  type AssetRecord,
  type Collection,
  type ExportPreset,
  type QueryTree,
  type RootRecord,
  type SavedSearch
} from "@focalfolio/domain-model";
import type { FileReference } from "@focalfolio/fs-contract";
import { useWorkspaceStore } from "./store";

export const APPLICATION_VERSION = "0.3.0";
export const fileSystem = new BrowserWorkspaceFileSystem();
export const jobs = new DurableJobRunner(catalog);

const previewCache = new Map<string, { url: string; fingerprint: string }>();
const MAX_PREVIEW_CACHE_ENTRIES = 200;
const messages: Array<{ level: string; message: string; at: number }> = [];
let writerRelease: (() => void) | undefined;

function log(level: string, message: string): void {
  messages.push({ level, message, at: Date.now() });
  if (messages.length > 500) messages.splice(0, messages.length - 500);
}

function storeNotice(level: "info" | "success" | "warning" | "error", message: string): void {
  log(level, message);
  useWorkspaceStore.getState().notice(level, message);
}

async function establishWriterLock(): Promise<void> {
  if (!navigator.locks) return;
  void navigator.locks.request("focalfolio-workspace-writer", { ifAvailable: true, mode: "exclusive" }, async (lock) => {
    if (!lock) {
      useWorkspaceStore.getState().set({ writer: false });
      storeNotice("warning", "Another FocalFolio tab owns the writable catalog. This tab is read-only.");
      return;
    }
    useWorkspaceStore.getState().set({ writer: true });
    await new Promise<void>((resolve) => {
      writerRelease = resolve;
    });
  });
  addEventListener("beforeunload", () => writerRelease?.(), { once: true });
}

export async function initializeWorkspace(): Promise<void> {
  const state = useWorkspaceStore.getState();
  const capabilities = detectRuntimeCapabilities();
  state.set({ compatibilityMode: !capabilities.fileSystemAccess });
  await establishWriterLock();

  const restored = await restoreRegisteredRoots().catch(() => [] as RegisteredRoot[]);
  for (const root of restored) fileSystem.register(root.record.id, root.handle);
  const catalogRoots = await catalog.getRoots();
  const records = new Map(catalogRoots.map((root) => [root.id, root]));
  for (const restoredRoot of restored) {
    const previous = records.get(restoredRoot.record.id);
    const record = { ...previous, ...restoredRoot.record } as RootRecord;
    records.set(record.id, record);
    await catalog.putRoot(record);
  }
  const roots = [...records.values()].sort((a, b) => a.name.localeCompare(b.name));
  const [collections, savedSearches, storedJobs] = await Promise.all([
    catalog.getCollections(),
    catalog.getSavedSearches(),
    catalog.getJobs()
  ]);
  await jobs.recover();
  const recoveredJobs = await catalog.getJobs();
  state.set({
    initialized: true,
    roots,
    activeRootId: roots[0]?.id,
    collections,
    savedSearches,
    jobs: recoveredJobs.length ? recoveredJobs : storedJobs
  });
  await catalog.requestPersistentStorage().catch(() => undefined);
  if (roots[0]) await refreshAssets();
}

export async function addLocalRoot(): Promise<void> {
  const registered = await pickDirectoryRoot();
  fileSystem.register(registered.record.id, registered.handle);
  await catalog.putRoot(registered.record);
  const roots = await catalog.getRoots();
  useWorkspaceStore.getState().set({ roots, activeRootId: registered.record.id, currentFolder: "" });
  storeNotice("success", `Authorized ${registered.record.name}.`);
  await scanRoot(registered.record.id);
}

export async function relinkRoot(root: RootRecord): Promise<void> {
  const registered = await pickDirectoryRoot({ id: `relink-${root.id}` });
  const selected = registered.handle;
  const handleRecord = await registerDirectoryHandle(selected);
  fileSystem.register(root.id, selected);
  const permission = await queryHandlePermission(selected);
  await catalog.putRoot({ ...root, name: selected.name, handleKey: handleRecord.record.handleKey, permission });
  await refreshRootState();
  storeNotice("success", `Relinked ${selected.name}.`);
  await scanRoot(root.id);
}

export async function requestRootAccess(root: RootRecord): Promise<void> {
  const handle = fileSystem.roots.get(root.id);
  if (!handle) return relinkRoot(root);
  const permission = await requestHandlePermission(handle);
  await catalog.putRoot({ ...root, permission });
  await refreshRootState();
  if (permission === "granted") await scanRoot(root.id);
}

export async function removeRoot(root: RootRecord): Promise<void> {
  fileSystem.unregister(root.id);
  await forgetRegisteredRoot(root.id).catch(() => undefined);
  await catalog.removeRoot(root.id, { removeAssets: false });
  await refreshRootState();
  storeNotice("info", `${root.name} was unlinked. Catalog records were retained for relinking.`);
}

async function refreshRootState(): Promise<void> {
  const state = useWorkspaceStore.getState();
  const roots = await catalog.getRoots();
  state.set({ roots, activeRootId: state.activeRootId && roots.some((root) => root.id === state.activeRootId) ? state.activeRootId : roots[0]?.id });
  await refreshAssets();
}

export async function scanRoot(rootId?: string): Promise<void> {
  const state = useWorkspaceStore.getState();
  const id = rootId ?? state.activeRootId;
  if (!id) return;
  const root = state.roots.find((candidate) => candidate.id === id);
  if (!fileSystem.roots.has(id)) {
    if (root) await relinkRoot(root);
    return;
  }
  const controller = new AbortController();
  state.set({ scanning: true, scanProgress: { discovered: 0, metadata: 0, phase: "Discovering files" } });
  const discoveredPaths = new Set<string>();
  const changed: AssetRecord[] = [];
  let batch: AssetRecord[] = [];
  try {
    for await (const entry of fileSystem.scan(id, { signal: controller.signal })) {
      if (entry.kind !== "file" || entry.name.toLocaleLowerCase().endsWith(".xmp")) continue;
      discoveredPaths.add(entry.relativePath);
      const existing = await catalog.getAssetByPath(id, entry.relativePath);
      const asset = assetFromFileEntry(id, entry, existing);
      batch.push(asset);
      if (!existing || existing.fingerprint !== asset.fingerprint) changed.push(asset);
      if (batch.length >= 100) {
        await catalog.upsertAssets(batch);
        batch = [];
      }
      state.set({ scanProgress: { discovered: discoveredPaths.size, metadata: 0, phase: "Discovering files" } });
    }
    await catalog.upsertAssets(batch);
    await catalog.markMissingAssets(id, discoveredPaths);
    const updatedRoot: RootRecord = { ...(root ?? (await catalog.getRoot(id))!), lastScanAt: Date.now(), permission: "granted" };
    await catalog.putRoot(updatedRoot);

    state.set({ scanProgress: { discovered: discoveredPaths.size, metadata: 0, phase: "Reading local metadata" } });
    let metadataDone = 0;
    await runBoundedBatch({
      items: changed.filter((asset) => asset.size <= 512 * 1024 * 1024),
      concurrency: 2,
      worker: async (asset) => {
        await hydrateMetadata(asset).catch((error) => log("warning", `Metadata ${asset.filename}: ${error instanceof Error ? error.message : String(error)}`));
      },
      onProgress: (completed) => {
        metadataDone = completed;
        state.set({ scanProgress: { discovered: discoveredPaths.size, metadata: completed, phase: "Reading local metadata" } });
      }
    });
    log("info", `Scanned ${discoveredPaths.size} files; refreshed metadata for ${metadataDone}.`);
    await refreshRootState();
    storeNotice("success", `Scan complete: ${discoveredPaths.size.toLocaleString()} files indexed locally.`);
  } catch (error) {
    storeNotice("error", error instanceof Error ? error.message : String(error));
  } finally {
    state.set({ scanning: false, scanProgress: { discovered: discoveredPaths.size, metadata: 0, phase: "Idle" } });
  }
}

async function hydrateMetadata(asset: AssetRecord): Promise<AssetRecord> {
  const source: FileReference = { rootId: asset.rootId, relativePath: asset.relativePath };
  const file = await fileSystem.getFile(source);
  const result = await readMetadata(file);
  let sidecarUpdate: XmpUpdate | undefined;
  try {
    const sidecar = await fileSystem.getFile({ rootId: asset.rootId, relativePath: sidecarPathFor(asset.relativePath) });
    sidecarUpdate = parseXmp(await sidecar.text());
  } catch {
    sidecarUpdate = undefined;
  }
  const updated: AssetRecord = {
    ...asset,
    rating: (sidecarUpdate?.rating ?? asset.rating) as AssetRecord["rating"],
    label: sidecarUpdate?.label ?? asset.label,
    metadata: {
      ...asset.metadata,
      ...result.metadata,
      ...(sidecarUpdate?.metadata ?? {}),
      keywords: sidecarUpdate?.metadata?.keywords?.length ? sidecarUpdate.metadata.keywords : result.metadata.keywords
    },
    ...(sidecarUpdate?.adjustment ? { adjustment: sidecarUpdate.adjustment } : {}),
    updatedAt: Date.now()
  };
  await catalog.putAsset(updated);
  return updated;
}

export async function refreshAssets(overrides: Partial<AssetQuery> = {}): Promise<void> {
  const state = useWorkspaceStore.getState();
  if (!state.activeRootId) {
    state.set({ assets: [], totalAssets: 0 });
    return;
  }
  const result = await catalog.queryAssets({
    rootId: state.activeRootId,
    parentPath: state.currentFolder,
    text: state.search,
    minimumRating: state.minimumRating,
    labels: state.labelFilter === "all" ? undefined : [state.labelFilter],
    kinds: state.kindFilter === "all" ? undefined : [state.kindFilter],
    sort: state.sort,
    direction: state.direction,
    limit: 20_000,
    ...overrides
  });
  state.set({ assets: result.assets, totalAssets: result.total, selectedIds: state.selectedIds.filter((id) => result.assets.some((asset) => asset.id === id)) });
}

export async function selectFolder(folder: string): Promise<void> {
  useWorkspaceStore.getState().set({ currentFolder: folder, selectedIds: [], primaryId: undefined });
  await refreshAssets();
}

export async function localPreviewUrl(asset: AssetRecord): Promise<string | undefined> {
  const key = asset.id;
  const existing = previewCache.get(key);
  if (existing?.fingerprint === asset.fingerprint) {
    previewCache.delete(key);
    previewCache.set(key, existing);
    return existing.url;
  }
  if (existing) URL.revokeObjectURL(existing.url);
  const file = await fileSystem.getFile({ rootId: asset.rootId, relativePath: asset.relativePath });
  const blob = await createLocalPreviewBlob(file, asset.kind);
  if (!blob) return undefined;
  const url = URL.createObjectURL(blob);
  previewCache.set(key, { url, fingerprint: asset.fingerprint });
  while (previewCache.size > MAX_PREVIEW_CACHE_ENTRIES) {
    const oldestKey = previewCache.keys().next().value as string | undefined;
    if (!oldestKey) break;
    const oldest = previewCache.get(oldestKey);
    if (oldest) URL.revokeObjectURL(oldest.url);
    previewCache.delete(oldestKey);
  }
  return url;
}

async function localFileExists(reference: FileReference): Promise<boolean> {
  try {
    await fileSystem.getFile(reference);
    return true;
  } catch (error) {
    if (error instanceof DOMException && error.name === "NotFoundError") return false;
    return false;
  }
}

export async function sourceObjectUrl(asset: AssetRecord): Promise<string> {
  const file = await fileSystem.getFile({ rootId: asset.rootId, relativePath: asset.relativePath });
  return URL.createObjectURL(file);
}

export async function updateAsset(asset: AssetRecord, update: XmpUpdate): Promise<AssetRecord> {
  if (!useWorkspaceStore.getState().writer) throw new Error("This tab is read-only while another FocalFolio tab is open.");
  const next: AssetRecord = {
    ...asset,
    rating: (update.rating ?? asset.rating) as AssetRecord["rating"],
    label: update.label ?? asset.label,
    metadata: { ...asset.metadata, ...(update.metadata ?? {}), keywords: update.metadata?.keywords ?? asset.metadata.keywords },
    adjustment: update.adjustment ?? asset.adjustment,
    updatedAt: Date.now()
  };
  await writeXmpSidecarSafely(fileSystem, { rootId: asset.rootId, relativePath: asset.relativePath }, {
    rating: next.rating,
    label: next.label,
    metadata: next.metadata,
    ...(next.adjustment ? { adjustment: next.adjustment } : {})
  });
  await catalog.putAsset(next);
  await refreshAssets();
  return next;
}

export async function batchUpdateMetadata(assets: AssetRecord[], update: XmpUpdate): Promise<void> {
  useWorkspaceStore.getState().set({ busy: true, busyMessage: `Updating metadata for ${assets.length} files` });
  try {
    await runBoundedBatch({ items: assets, concurrency: 2, worker: async (asset) => { await updateAsset(asset, update); } });
    storeNotice("success", `Updated ${assets.length} sidecar${assets.length === 1 ? "" : "s"}.`);
  } finally {
    useWorkspaceStore.getState().set({ busy: false, busyMessage: "" });
  }
}

export async function executeBatchRename(assets: AssetRecord[], options: RenameTemplateOptions): Promise<void> {
  const plan = planBatchRename(assets, options);
  const conflicts = plan.filter((item) => item.conflict);
  if (conflicts.length) throw new Error(conflicts.map((item) => `${item.sourceFilename}: ${item.conflict}`).join("\n"));

  const sidecars = new Map<string, { source: FileReference; destination: FileReference }>();
  const sourceSidecarPaths = new Set<string>();
  for (const item of plan) {
    if (item.source.relativePath === item.destination.relativePath) continue;
    const source: FileReference = { rootId: item.source.rootId, relativePath: sidecarPathFor(item.source.relativePath) };
    if (!(await localFileExists(source))) continue;
    const destination: FileReference = { rootId: item.destination.rootId, relativePath: sidecarPathFor(item.destination.relativePath) };
    sidecars.set(item.assetId, { source, destination });
    sourceSidecarPaths.add(`${source.rootId}:${source.relativePath.toLocaleLowerCase("en-US")}`);
  }
  for (const sidecar of sidecars.values()) {
    if (sidecar.source.relativePath === sidecar.destination.relativePath) continue;
    const destinationKey = `${sidecar.destination.rootId}:${sidecar.destination.relativePath.toLocaleLowerCase("en-US")}`;
    if (!sourceSidecarPaths.has(destinationKey) && await localFileExists(sidecar.destination)) {
      throw new Error(`Sidecar destination already exists: ${sidecar.destination.relativePath}`);
    }
  }

  const job = await jobs.create("batch-rename", { plan, sidecars: [...sidecars.values()] }, plan.length, "Rename preflight complete");
  useWorkspaceStore.getState().set({ busy: true, busyMessage: `Renaming ${plan.length} files` });
  try {
    const staged = new Map<string, { media: FileReference; sidecar?: FileReference }>();
    for (let index = 0; index < plan.length; index += 1) {
      const item = plan[index];
      if (!item || item.source.relativePath === item.destination.relativePath) continue;
      const temporary: FileReference = item.temporary ?? {
        rootId: item.source.rootId,
        relativePath: `${item.source.relativePath}.focalfolio-stage-${crypto.randomUUID()}`
      };
      await fileSystem.move(item.source, temporary);
      const sidecar = sidecars.get(item.assetId);
      let stagedSidecar: FileReference | undefined;
      if (sidecar && sidecar.source.relativePath !== sidecar.destination.relativePath) {
        stagedSidecar = {
          rootId: sidecar.source.rootId,
          relativePath: `${parentPathOf(sidecar.source.relativePath) ? `${parentPathOf(sidecar.source.relativePath)}/` : ""}.focalfolio-sidecar-stage-${crypto.randomUUID()}.tmp`
        };
        await fileSystem.move(sidecar.source, stagedSidecar);
      }
      staged.set(item.assetId, { media: temporary, ...(stagedSidecar ? { sidecar: stagedSidecar } : {}) });
      await catalog.putJob({ ...job, status: "running", progress: (index + 0.5) / plan.length, completedItems: index, message: `Staging ${item.sourceFilename}`, updatedAt: Date.now() });
    }
    for (let index = 0; index < plan.length; index += 1) {
      const item = plan[index];
      if (!item || item.source.relativePath === item.destination.relativePath) continue;
      const temporary = staged.get(item.assetId);
      if (!temporary) throw new Error(`Missing staged rename for ${item.sourceFilename}.`);
      await fileSystem.move(temporary.media, item.destination);
      const sidecar = sidecars.get(item.assetId);
      if (sidecar && temporary.sidecar) await fileSystem.move(temporary.sidecar, sidecar.destination);
      const asset = assets.find((candidate) => candidate.id === item.assetId);
      if (asset) {
        await catalog.deleteAsset(asset.id);
        await catalog.putAsset({
          ...asset,
          id: asset.id,
          relativePath: item.destination.relativePath,
          parentPath: parentPathOf(item.destination.relativePath),
          filename: item.destinationFilename,
          updatedAt: Date.now()
        });
      }
      await catalog.putJob({ ...job, status: "running", progress: (index + 1) / plan.length, completedItems: index + 1, message: `Committed ${item.destinationFilename}`, updatedAt: Date.now() });
    }
    await catalog.putJob({ ...job, status: "completed", progress: 1, completedItems: plan.length, message: "Completed", updatedAt: Date.now() });
    await refreshAssets();
    storeNotice("success", `Renamed ${plan.length} files and their available XMP sidecars.`);
  } catch (error) {
    await catalog.putJob({ ...job, status: "needs-user-action", error: error instanceof Error ? error.message : String(error), message: "Rename interrupted; inspect staged files.", updatedAt: Date.now() });
    throw error;
  } finally {
    useWorkspaceStore.getState().set({ busy: false, busyMessage: "", jobs: await catalog.getJobs() });
  }
}

export async function trashAssets(assets: AssetRecord[]): Promise<void> {
  useWorkspaceStore.getState().set({ busy: true, busyMessage: `Moving ${assets.length} files to FocalFolio Trash` });
  try {
    for (const asset of assets) {
      const media: FileReference = { rootId: asset.rootId, relativePath: asset.relativePath };
      const sidecar: FileReference = { rootId: asset.rootId, relativePath: sidecarPathFor(asset.relativePath) };
      const hasSidecar = await localFileExists(sidecar);
      const mediaTrash = await fileSystem.trash(media);
      try {
        if (hasSidecar) await fileSystem.trash(sidecar);
      } catch (error) {
        await fileSystem.restoreTrash(asset.rootId, mediaTrash.trashPath).catch(() => undefined);
        throw error;
      }
      await catalog.deleteAsset(asset.id);
    }
    useWorkspaceStore.getState().clearSelection();
    await refreshAssets();
    storeNotice("success", `Moved ${assets.length} file${assets.length === 1 ? "" : "s"} and available XMP sidecars to FocalFolio Trash.`);
  } finally {
    useWorkspaceStore.getState().set({ busy: false, busyMessage: "" });
  }
}

async function selectedPreviewItems(assets: AssetRecord[]): Promise<Array<{ asset: AssetRecord; blob: Blob }>> {
  const output: Array<{ asset: AssetRecord; blob: Blob }> = [];
  for (const asset of assets) {
    const file = await fileSystem.getFile({ rootId: asset.rootId, relativePath: asset.relativePath });
    const blob = await createLocalPreviewBlob(file, asset.kind);
    if (blob) output.push({ asset, blob });
  }
  return output;
}

export async function exportSelected(assets: AssetRecord[], preset: ExportPreset = DEFAULT_EXPORT_PRESET): Promise<void> {
  if (!assets.length) throw new Error("Select at least one previewable asset.");
  const destination = await pickDirectoryRoot({ id: "focalfolio-export" });
  fileSystem.register(destination.record.id, destination.handle);
  const items = await selectedPreviewItems(assets);
  useWorkspaceStore.getState().set({ busy: true, busyMessage: `Exporting ${items.length} files locally` });
  try {
    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      if (!item) continue;
      await exportAsset({ fileSystem, asset: item.asset, sourceBlob: item.blob, destinationRootId: destination.record.id, destinationDirectory: "", preset, sequence: index + 1 });
    }
    storeNotice("success", `Exported ${items.length} files to ${destination.record.name}.`);
  } finally {
    fileSystem.unregister(destination.record.id);
    useWorkspaceStore.getState().set({ busy: false, busyMessage: "" });
  }
}

export async function importFromFolder(destinationRootId: string): Promise<void> {
  const source = await pickDirectoryRoot({ id: "focalfolio-import-source", mode: "read" });
  fileSystem.register(source.record.id, source.handle);
  const date = new Date().toISOString().slice(0, 10);
  const destinationDirectory = `Imported/${date}`;
  useWorkspaceStore.getState().set({ busy: true, busyMessage: "Discovering import source" });
  let imported = 0;
  try {
    for await (const entry of fileSystem.scan(source.record.id)) {
      if (entry.kind !== "file") continue;
      const destinationPath = `${destinationDirectory}/${entry.relativePath}`;
      await fileSystem.copyVerified(
        { rootId: source.record.id, relativePath: entry.relativePath },
        { rootId: destinationRootId, relativePath: destinationPath },
        { digest: true }
      );
      imported += 1;
      useWorkspaceStore.getState().set({ busyMessage: `Imported and verified ${imported} files` });
    }
    await scanRoot(destinationRootId);
    storeNotice("success", `Imported ${imported} files with SHA-256 verification.`);
  } finally {
    fileSystem.unregister(source.record.id);
    useWorkspaceStore.getState().set({ busy: false, busyMessage: "" });
  }
}

export async function downloadContactSheet(assets: AssetRecord[]): Promise<void> {
  const items = await selectedPreviewItems(assets);
  const pdf = await createContactSheetPdf(items, { title: "FocalFolio Contact Sheet", caption: "filename" });
  downloadBlob(pdf, `focalfolio-contact-sheet-${new Date().toISOString().slice(0, 10)}.pdf`);
}

export async function downloadWebGallery(assets: AssetRecord[]): Promise<void> {
  const items = await selectedPreviewItems(assets);
  const zip = await createWebGalleryZip(items, { title: "FocalFolio Gallery", includeMetadata: true });
  downloadBlob(zip, `focalfolio-gallery-${new Date().toISOString().slice(0, 10)}.zip`);
}

export async function downloadCatalogBackup(): Promise<void> {
  const backup = await catalog.exportBackup(APPLICATION_VERSION);
  downloadBlob(new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" }), `focalfolio-${new Date().toISOString().slice(0, 10)}.ffcatalog`);
}

export async function restoreCatalogBackup(file: File, mode: "merge" | "replace"): Promise<void> {
  const backup = JSON.parse(await file.text()) as CatalogBackup;
  await catalog.importBackup(backup, mode);
  await refreshRootState();
  storeNotice("success", "Catalog restored. Relink local roots before rescanning.");
}

export async function createManualCollection(name: string, assetIds: string[]): Promise<void> {
  const now = Date.now();
  const collection: Collection = { id: `collection_${crypto.randomUUID()}`, name, kind: "manual", assetIds, createdAt: now, updatedAt: now };
  await catalog.putCollection(collection);
  useWorkspaceStore.getState().set({ collections: await catalog.getCollections() });
}

export async function createSmartCollection(name: string, query: QueryTree): Promise<void> {
  const now = Date.now();
  const collection: Collection = { id: `collection_${crypto.randomUUID()}`, name, kind: "smart", assetIds: [], query, createdAt: now, updatedAt: now };
  await catalog.putCollection(collection);
  useWorkspaceStore.getState().set({ collections: await catalog.getCollections() });
}

export async function openCollection(collection: Collection): Promise<void> {
  const state = useWorkspaceStore.getState();
  const all = await catalog.queryAssets({ rootId: state.activeRootId, limit: 25_000, queryTree: collection.query });
  const assets = collection.kind === "manual" ? all.assets.filter((asset) => collection.assetIds.includes(asset.id)) : all.assets;
  state.set({ currentFolder: "", assets, totalAssets: assets.length, selectedIds: [] });
}

export async function saveCurrentSearch(name: string): Promise<void> {
  const state = useWorkspaceStore.getState();
  const rules: QueryTree["rules"] = [];
  if (state.search) rules.push({ field: "filename", comparison: "contains", value: state.search });
  if (state.minimumRating) rules.push({ field: "rating", comparison: "greaterThanOrEqual", value: state.minimumRating });
  if (state.labelFilter !== "all") rules.push({ field: "label", comparison: "equals", value: state.labelFilter });
  if (state.kindFilter !== "all") rules.push({ field: "kind", comparison: "equals", value: state.kindFilter });
  const now = Date.now();
  const saved: SavedSearch = { id: `search_${crypto.randomUUID()}`, name, query: { version: 1, operator: "and", rules }, createdAt: now, updatedAt: now };
  await catalog.putSavedSearch(saved);
  state.set({ savedSearches: await catalog.getSavedSearches() });
}

export async function applySavedSearch(search: SavedSearch): Promise<void> {
  const textRule = search.query.rules.find((rule) => rule.field === "filename" && rule.comparison === "contains");
  const ratingRule = search.query.rules.find((rule) => rule.field === "rating");
  const labelRule = search.query.rules.find((rule) => rule.field === "label");
  const kindRule = search.query.rules.find((rule) => rule.field === "kind");
  useWorkspaceStore.getState().set({
    search: typeof textRule?.value === "string" ? textRule.value : "",
    minimumRating: typeof ratingRule?.value === "number" ? ratingRule.value : 0,
    labelFilter: (labelRule?.value as ReturnType<typeof useWorkspaceStore.getState>["labelFilter"]) ?? "all",
    kindFilter: typeof kindRule?.value === "string" ? kindRule.value : "all"
  });
  await refreshAssets({ queryTree: search.query });
}

export async function downloadDiagnostics(): Promise<void> {
  const state = useWorkspaceStore.getState();
  const report = createDiagnosticReport({
    applicationVersion: APPLICATION_VERSION,
    roots: state.roots.map((root) => ({ id: root.id, name: root.name, permission: root.permission })),
    assetCount: state.totalAssets,
    jobs: state.jobs.map((job) => ({ id: job.id, type: job.type, status: job.status, error: job.error })),
    messages
  });
  downloadBlob(new Blob([JSON.stringify(report, null, 2)], { type: "application/json" }), `focalfolio-diagnostics-${Date.now()}.json`);
}

export function selectedAssets(): AssetRecord[] {
  const state = useWorkspaceStore.getState();
  return state.assets.filter((asset) => state.selectedIds.includes(asset.id));
}
